import { Abs, Max, Min } from "@pdfking/math"
import { first } from "@pdfking/rfunc"
/**
 * Scroll component for horizontal scrolling with arrow controls
 *
 * Usage:
 * <button data-component="scroll" data-target="#scrollable-element" data-direction="left">←</button>
 * <button data-component="scroll" data-target="#scrollable-element" data-direction="right">→</button>
 * <div id="scrollable-element" class="row flex-nowrap" style="transform: translateX(0px); transition: transform 0.3s ease;">
 *   <!-- content -->
 * </div>
 *
 * Gesture scrolling:
 * <div data-component="scroll_gesture" class="row flex-nowrap" style="transform: translateX(0px); transition: transform 0.3s ease;">
 *   <!-- content -->
 * </div>
 */
import append from "../dom/append"
import element from "../dom/element"
import selector from "../dom/selector"
import toggle_class from "../dom/toggle_class"
import wrap from "../dom/wrap"
import can_hover from "../features/can_hover"

// Shared utility functions
const getCurrentTranslateX = element => {
  const transform = element.style.transform
  const match = transform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/)
  return match ? parseFloat(match[1]) : 0
}

const getCoords = element => {
  return {
    inner: element.offsetWidth,
    outer: element.scrollWidth,
  }
}

const _clampTranslateX = (element, value) => {
  const { inner, outer } = getCoords(element)
  const minTranslate = -(outer - inner)
  return Max(Min(value, 0), minTranslate)
}

const updateButtons = (element, { left, right }) => {
  if (can_hover()) {
    const translateX = getCurrentTranslateX(element)
    const { inner, outer } = getCoords(element)

    const showLeft = translateX < 0
    const showRight = translateX > -(outer - inner)
    const hide = !showLeft && !showRight

    toggle_class(left, {
      "d-none": hide,
      "disabled opacity-25": !showLeft,
    })

    toggle_class(right, {
      "d-none": hide,
      "disabled opacity-25": !showRight,
    })
  }
}

const initializeStyles = element => {
  if (!element.style.transform) {
    element.style.transform = "translateX(0px)"
  }
  if (!element.style.transition) {
    element.style.transition = "transform 0.3s ease"
  }
}

const handleScroll = ({ arrows, container, direction, sizeElement }) => {
  const translateX = getCurrentTranslateX(container)
  const { inner, outer } = getCoords(container)

  const scrollAmount = sizeElement ? sizeElement.offsetWidth : inner
  let newTranslateX

  if (direction === "left") {
    newTranslateX = Min(translateX + scrollAmount, 0)
  } else if (direction === "right") {
    newTranslateX = Max(translateX - scrollAmount, -(outer - inner))
  }

  container.style.transform = `translateX(${newTranslateX}px)`

  // Update buttons after animation
  setTimeout(() => updateButtons(container, arrows), 50)
}

function scroll(container) {
  const sizeSelector = container.getAttribute("data-scroll-size")
  const sizeElement = sizeSelector ? first(selector(sizeSelector)) : null

  // Create left arrow
  const left = element(
    "div",
    {
      class:
        "position-absolute top-0 start-0 d-flex align-items-center justify-content-center h-100 clickable onhover-show-visibility",
      style: "transition: all 0.3s linear",
    },
    element(
      "div",
      {
        class:
          "btn btn-outline-primary btn-lg bg-translucent rounded-circle mx-3",
      },
      element("i", { class: "icon icon-CHEVRON_LEFT" })
    )
  )

  // Create right arrow
  const right = element(
    "div",
    {
      class:
        "position-absolute top-0 end-0 d-flex align-items-center justify-content-center h-100 clickable onhover-show-visibility",
      style: "transition: all 0.3s linear",
    },
    element(
      "div",
      {
        class:
          "btn btn-outline-primary btn-lg bg-translucent rounded-circle mx-3",
      },
      element("i", { class: "icon icon-CHEVRON_RIGHT" })
    )
  )

  // Create wrapper with position-relative and onhover
  const wrapper = element("div", {
    class: "position-relative onhover",
  })

  // Wrap the container
  wrap(container, wrapper)

  append(wrapper, [left, right])

  // Store arrow references
  const arrows = { left, right }

  initializeStyles(container)

  // Add touch gesture handlers to scrollable element
  let startX = 0

  const handleStart = e => {
    const { inner, outer } = getCoords(container)
    if (outer <= inner) {
      return
    }
    startX = e.touches[0].clientX
  }

  const handleEnd = e => {
    const endX = e.changedTouches[0].clientX
    const deltaX = endX - startX

    if (Abs(deltaX) < 20) {
      return
    }

    const direction = deltaX > 0 ? "left" : "right"
    handleScroll({
      arrows,
      container,
      direction,
      sizeElement,
    })
  }

  container.addEventListener("touchstart", handleStart)
  container.addEventListener("touchend", handleEnd)

  for (const [arrow, direction] of [
    [left, "left"] as const,
    [right, "right"] as const,
  ]) {
    arrow.addEventListener("click", () =>
      handleScroll({
        arrows,
        container,
        direction,
        sizeElement,
      })
    )
  }

  // Initial button state update
  requestAnimationFrame(() => updateButtons(container, arrows))
}

export const Scroll = scroll
